import { ActionReducer, MetaReducer } from '@ngrx/store';
import { PersistedSlice } from './persisted-state';
import { StorageActions } from './storage.actions';

type RootState = Record<string, unknown>;

/**
 * Writes the stored payloads into their slices when `hydrated` arrives.
 *
 * A feature reducer only ever sees its own slice, so a single action cannot fill several
 * of them from inside the reducers — that is what a meta-reducer is for. Doing it here
 * rather than per feature also means a newly registered slice cannot forget to handle
 * hydration: the registry is the only place that has to know about it.
 *
 * Every other action passes straight through untouched.
 */
export const hydrationMetaReducer =
  (slices: readonly PersistedSlice<unknown>[]): MetaReducer<RootState> =>
  (reducer: ActionReducer<RootState>): ActionReducer<RootState> =>
  (state, action) => {
    const next = reducer(state, action);
    if (action.type !== StorageActions.hydrated.type) return next;

    const { slices: stored } = action as ReturnType<typeof StorageActions.hydrated>;
    const hydrated: RootState = { ...next };
    for (const slice of slices) {
      hydrated[slice.name] = slice.reshape(stored[slice.name]);
    }
    return hydrated;
  };
